import type { Board, BoardDef, Cell } from './types';

export type GridType = NonNullable<BoardDef['grid']>;

/** What the geometry needs to know about a board; a parsed `Board` qualifies. */
export interface Grid {
  readonly width: number;
  readonly height: number;
  readonly def: Pick<BoardDef, 'grid'>;
}

export interface Coord {
  readonly x: number;
  readonly y: number;
}

/**
 * Board geometry in one place: which cells touch, how far apart cells are and which cells a
 * flier crosses. Square boards use the four orthogonal neighbours. Hex boards keep the same row
 * strings but read them as "odd-r" offset coordinates: pointy-top hexes in horizontal rows, with
 * every odd row shifted half a cell to the east, so each hex touches two cells in its own row and
 * two in each of the rows above and below.
 */
export function gridOf(board: Grid): GridType {
  return board.def.grid ?? 'square';
}

/** The most sides a board can seat: four on a square board, six on a hex board. */
export function maxFactions(grid: GridType): number {
  return grid === 'hex' ? 6 : 4;
}

export function isInside(board: Pick<Grid, 'width' | 'height'>, x: number, y: number): boolean {
  return (
    Number.isInteger(x) &&
    Number.isInteger(y) &&
    x >= 0 &&
    y >= 0 &&
    x < board.width &&
    y < board.height
  );
}

/** Row-major index of a cell; callers must check `isInside` first. */
export function cellIndex(board: Pick<Grid, 'width'>, x: number, y: number): number {
  return y * board.width + x;
}

const SQUARE_STEPS = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;
/** Odd-r: an even row's diagonal neighbours lie to the west, an odd row's to the east. */
const HEX_STEPS_EVEN = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [-1, 1],
  [0, -1],
  [-1, -1],
] as const;
const HEX_STEPS_ODD = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [1, 1],
  [0, -1],
  [1, -1],
] as const;

/** Offsets to the cells that share an edge with a cell in row `y`. */
export function steps(board: Grid, y: number): readonly (readonly [number, number])[] {
  if (gridOf(board) === 'square') return SQUARE_STEPS;
  return y & 1 ? HEX_STEPS_ODD : HEX_STEPS_EVEN;
}

/**
 * Cells sharing an edge with (x, y) that lie on the board: up to four on a square grid, six on a
 * hex grid. Void cells are included; the rules filter them.
 */
export function neighbours(board: Board, x: number, y: number): Cell[] {
  const out: Cell[] = [];
  for (const [dx, dy] of steps(board, y)) {
    const nx = x + dx;
    const ny = y + dy;
    if (isInside(board, nx, ny)) out.push(board.cells[cellIndex(board, nx, ny)]!);
  }
  return out;
}

export function areNeighbours(board: Grid, a: Coord, b: Coord): boolean {
  return steps(board, a.y).some(([dx, dy]) => a.x + dx === b.x && a.y + dy === b.y);
}

/**
 * Distance as the rules count flying and teleport ranges: on a square grid diagonals count as
 * one step (Chebyshev), on a hex grid it is the number of hexes stepped through.
 */
export function cellDistance(board: Grid, a: Coord, b: Coord): number {
  if (gridOf(board) === 'square') return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
  const [aq, ar, as] = toCube(a.x, a.y);
  const [bq, br, bs] = toCube(b.x, b.y);
  return Math.max(Math.abs(aq - bq), Math.abs(ar - br), Math.abs(as - bs));
}

/** Steps a ground piece needs on an empty board: Manhattan on squares, hex distance on hexes. */
export function walkDistance(board: Grid, a: Coord, b: Coord): number {
  if (gridOf(board) === 'square') return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  return cellDistance(board, a, b);
}

/** Cells within `range` of (x, y) by `cellDistance`, row by row, the centre excluded. */
export function cellsWithin(board: Board, x: number, y: number, range: number): Cell[] {
  const out: Cell[] = [];
  const centre = { x, y };
  for (let cy = y - range; cy <= y + range; cy++) {
    for (let cx = x - range; cx <= x + range; cx++) {
      if ((cx === x && cy === y) || !isInside(board, cx, cy)) continue;
      if (cellDistance(board, centre, { x: cx, y: cy }) > range) continue;
      out.push(board.cells[cellIndex(board, cx, cy)]!);
    }
  }
  return out;
}

/**
 * The cells a flier crosses from `a` to `b`, excluding `a` and including `b`: diagonal-first steps
 * on a square grid, the straight hex line on a hex grid. Purely for presentation.
 */
export function line(board: Grid, a: Coord, b: Coord): Coord[] {
  const out: Coord[] = [];
  if (gridOf(board) === 'square') {
    let x = a.x;
    let y = a.y;
    while (x !== b.x || y !== b.y) {
      x += Math.sign(b.x - x);
      y += Math.sign(b.y - y);
      out.push({ x, y });
    }
    return out;
  }
  const n = cellDistance(board, a, b);
  const [aq, ar, as] = toCube(a.x, a.y);
  const [bq, br, bs] = toCube(b.x, b.y);
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    // A nudge off the exact midpoints keeps a line that runs along hex edges on one side of them.
    out.push(
      fromCube(
        ...cubeRound(
          aq + (bq - aq) * t + 1e-6,
          ar + (br - ar) * t + 1e-6,
          as + (bs - as) * t - 2e-6,
        ),
      ),
    );
  }
  return out;
}

/**
 * Cube coordinates (q + r + s = 0) of an odd-r offset hex, where distances and translations are
 * simple: moving a shape by a cube vector keeps its neighbours, which a shift in offset
 * coordinates does not when it changes the row parity.
 */
export function toCube(x: number, y: number): [number, number, number] {
  const q = x - (y - (y & 1)) / 2;
  return [q, y, -q - y];
}

/** The odd-r offset hex of cube coordinates (q, r); `s` follows from the other two. */
export function fromCube(q: number, r: number): Coord {
  return { x: q + (r - (r & 1)) / 2, y: r };
}

/** Floor cells of a hexagon of hexes: 1, 7, 19, 37, 61, 91, … for radius 0, 1, 2, … */
export function hexagonCellCount(radius: number): number {
  return 3 * radius * (radius + 1) + 1;
}

/**
 * Row strings of a hexagon of hexes – `radius` rings around a centre cell – in odd-r
 * coordinates, on a grid of 2·radius + 1 rows and columns with the centre cell at
 * (radius, radius). Row y holds n = 2·radius + 1 − |y − radius| floor cells; since odd rows are
 * drawn half a cell east, the run that keeps every row centred under the centre cell starts at
 * x = radius − ⌊(n − 1 + (y & 1)) / 2⌋. The other cells are void (`#`). Radius 2:
 *
 *     #LLL#
 *     LLLL#
 *     LLLLL
 *     LLLL#
 *     #LLL#
 *
 * Laid out as pointy-top hexes the shape is a flat-top hexagon: its first and last rows are its
 * north and south sides, its corners point east and west, and the other four sides are the
 * staircases in between. `fill` chooses the character of each floor cell.
 */
export function hexagonCells(
  radius: number,
  fill: (x: number, y: number) => string = () => 'L',
): string[] {
  const size = 2 * radius + 1;
  const rows: string[] = [];
  for (let y = 0; y < size; y++) {
    const n = size - Math.abs(y - radius);
    const start = radius - Math.floor((n - 1 + (y & 1)) / 2);
    let row = '';
    for (let x = 0; x < size; x++)
      row += x >= start && x < start + n ? fill(x, y).charAt(0) || 'L' : '#';
    rows.push(row);
  }
  return rows;
}

/**
 * The radius of the hexagon a hex board outlines, or `undefined` when it does not outline one:
 * the grid must be square with an odd side 2·radius + 1 and every cell beyond `radius` steps
 * from the centre cell must be void. What lies inside is the designer's business (a void centre
 * makes a ring), so this is about the outline the table frames, not the floor.
 */
export function hexagonRadius(
  def: Pick<BoardDef, 'grid' | 'width' | 'height' | 'cells'>,
): number | undefined {
  if (def.grid !== 'hex' || def.width !== def.height || def.width % 2 === 0) return undefined;
  const radius = (def.width - 1) / 2;
  const grid = { width: def.width, height: def.height, def: { grid: 'hex' as const } };
  const centre = { x: radius, y: radius };
  for (let y = 0; y < def.height; y++)
    for (let x = 0; x < def.width; x++)
      if (def.cells[y]?.[x] !== '#' && cellDistance(grid, centre, { x, y }) > radius)
        return undefined;
  return radius;
}

function cubeRound(q: number, r: number, s: number): [number, number] {
  let rq = Math.round(q);
  let rr = Math.round(r);
  const rs = Math.round(s);
  const dq = Math.abs(rq - q);
  const dr = Math.abs(rr - r);
  const ds = Math.abs(rs - s);
  if (dq > dr && dq > ds) rq = -rr - rs;
  else if (dr > ds) rr = -rq - rs;
  return [rq, rr];
}
